/**
 * The language, for a component that lets someone change it.
 *
 * Goes through `useTranslation` rather than reading the i18next singleton, so
 * the component re-renders when the language changes from anywhere — including
 * a second selector on another part of the screen.
 */
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { FALLBACK_LANGUAGE, LANGUAGES, isLanguageCode, type Language, type LanguageCode } from './config';
import { changeLanguage } from './index';

export interface UseLanguage {
  /** The active language. Never a regional tag — always one this build ships. */
  language: LanguageCode;
  /** Everything selectable, in the order the selector should list it. */
  languages: readonly Language[];
  setLanguage: (code: LanguageCode) => void;
}

export function useLanguage(): UseLanguage {
  const { i18n } = useTranslation();

  const setLanguage = useCallback((code: LanguageCode) => {
    void changeLanguage(code);
  }, []);

  return {
    language: isLanguageCode(i18n.language) ? i18n.language : FALLBACK_LANGUAGE,
    languages: LANGUAGES,
    setLanguage,
  };
}
